import {
    As,
    Box,
    Flex,
    OmitCommonProps,
    Spinner,
    SpinnerProps,
    Text,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { CSSProperties } from 'react';
import {
    ControlProps,
    PlaceholderProps,
    Props,
    components,
} from 'react-select';
import { OptionProps } from 'react-select/src/types';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import CanopyTooltip from '../CanopyTooltip/CanopyTooltip';
import Icon from '../Icon/Icon';

const getCommonStyles = (
    currentColors: ReturnType<typeof useCurrentColors>,
    { border, ...props }: any
) => ({
    valueContainer: (base: any, state: ControlProps<{}, boolean>) => ({
        ...base,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        padding: '0 8px',
        height: 'inherit',
        position: 'initial',
    }),
    singleValue: (
        provided: CSSProperties,
        state: ControlProps<{}, boolean>
    ) => ({
        ...provided,
        color: props?.color ? props.color : currentColors.brand['10'],
        fontSize: 13,
        fontWeight: 500,
        width: '100%',
    }),
    input: (provided: CSSProperties, state: ControlProps<{}, boolean>) => {
        return {
            ...provided,
            color: currentColors.inputText,
            fontSize: 13,
            fontWeight: 500,
            height: 'inherit',
        };
    },
    placeholder: (
        provided: CSSProperties,
        state: PlaceholderProps<{}, boolean>
    ) => ({
        ...provided,
        fontSize: 13,
        color: currentColors.whiteAlpha['500'],
    }),
    menu: (provided: CSSProperties, state: ControlProps<{}, boolean>) => ({
        ...provided,
        marginBottom: '8px',
        boxSizing: 'border-box',
        margin: 0,
        backgroundColor: currentColors.componentBg,
        boxShadow: props?.boxShadow || `1px 2px 2px ${currentColors.boxShadow}`,
        borderWidth: 1,
        // height: '300px',
        // top: '30px',
        zIndex: 9999,
        position: 'absolute',
        top: 'auto',
    }),
    menuPortal: (provided: CSSProperties) => ({
        ...provided,
        zIndex: 9999,
    }),
    indicatorsContainer: (provided: CSSProperties) => ({
        ...provided,
        cursor: 'pointer',
        height: 'inherit',
        alignItems: 'center',
        alignSelf: 'center',
    }),
    indicatorSeparator: (
        provided: CSSProperties,
        state: ControlProps<{}, boolean>
    ) => ({
        display: 'none',
    }),
    option: (provided: CSSProperties, state: OptionProps) => {
        const { isMultiLineOption } = props || {};
        const optionStyle = {
            ...provided,
            fontWeight: state.isSelected ? 700 : 400,
            position: 'relative',
            paddingLeft: 22,
            paddingRight: 22,
            '&:active': {
                backgroundColor: 'inherit',
            },

            '&::before': {
                position: 'absolute',
                content: "''",
                left: 8,
                top: 0,
                width: 'calc(100% - 16px)',
                backgroundColor: state.isFocused
                    ? currentColors.componentBorder
                    : 'inherit',
                borderRadius: 4,
                height: '100%',
                zIndex: -1,
            },
            cursor: 'pointer',
            whiteSpace: 'pre-wrap',
            fontSize: '11px',
            color: currentColors.brand['300'],
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: currentColors.tagBg,
            },
        };
        return isMultiLineOption
            ? {
                  ...optionStyle,
                  color: currentColors.brand['500'],
                  '&:first-line': {
                      color: currentColors.brand['300'],
                      pl: '-3px',
                      textIndent: '3px',
                  },
              }
            : optionStyle;
    },
    groupHeading: (provided: CSSProperties, state: OptionProps) => {
        return {
            ...provided,
            color: currentColors?.whiteAlpha['500'],
            textTransform: 'capitalize',
            fontSize: '11px',
        };
    },
});

const getDropdownIndicator = (
    currentColors: ReturnType<typeof useCurrentColors>
): any => {
    return ({ innerProps }: any) => {
        return (
            <Icon
                {...innerProps}
                color={currentColors.inputText}
                name={'ActDrpdwn'}
                width="12px"
                height="12px"
                mr="12px"
                cursor="pointer"
            />
        );
    };
};

const getMultiSelectStyles = (
    currentColors: ReturnType<typeof useCurrentColors>,
    props: any
) => {
    const commonStyles = getCommonStyles(currentColors, props);
    return {
        ...commonStyles,
        option: (base: any) => {
            const optionStyle = {
                ...base,
                whiteSpace: 'pre-wrap',
                fontSize: '11px',
                color: currentColors.brand['300'],
                pl: '-3px',
                textIndent: '3px',
                backgroundColor: 'transparent',
                '&:hover': {
                    backgroundColor: currentColors.tagBg,
                },
                cursor: 'pointer',
            };
            return props.isMultiLineOption
                ? {
                      ...optionStyle,
                      color: currentColors.brand['500'],
                      '&:first-line': {
                          color: currentColors.brand['300'],
                          pl: '0px',
                          textIndent: '0px',
                      },
                  }
                : optionStyle;
        },

        multiValue: (base: any) => ({
            ...base,
            display: 'flex',
            flexDirection: 'row-reverse',
            border: `1px solid ${currentColors.brand['700']}`,
            backgroundColor: currentColors.tagBg,
            color: currentColors.brand['300'],
            padding: '0 3px',
            borderRadius: '9999px',
            margin: '3px',
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            fontSize: '12px',
            padding: '0',
            color: currentColors.brand['300'],
            letterSpacing: '0.2px',
        }),
        noOptionsMessage: (base: any) => ({
            ...base,
            fontSize: '13px',
            letterSpacing: '0.2px',
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            cursor: 'pointer',
            '&:hover': {
                color: 'unset',
                backgroundColor: 'unset',
            },
            paddingRight: '4px',
            paddingLeft: '0px',
            color: currentColors.brand['500'],
        }),
        input: (
            provided: CSSProperties,
            state: Props<Record<string, unknown>, boolean>
        ) => {
            return {
                ...provided,
                position: 'relative',
                color: currentColors.brand['300'],
            };
        },
        menu: (provided: CSSProperties) => ({
            ...provided,
            marginBottom: '8px',
            boxSizing: 'border-box',
            margin: 0,
            backgroundColor: currentColors.componentBg,
            boxShadow:
                props?.boxShadow || `1px 2px 2px ${currentColors.boxShadow}`,
            borderWidth: 1,
            zIndex: 9999,
            position: 'absolute',
            top: 'auto',
            // height: '300px',
            // top: '30px',
        }),
    };
};

export const LoadingIndicator = (
    props: JSX.IntrinsicAttributes &
        OmitCommonProps<
            React.DetailedHTMLProps<
                React.HTMLAttributes<HTMLDivElement>,
                HTMLDivElement
            >,
            keyof SpinnerProps
        > &
        SpinnerProps &
        OmitCommonProps<any, keyof SpinnerProps> & {
            as?: As<any> | undefined;
        }
) => {
    return (
        <Spinner
            speed="1S"
            width="15px"
            height="15px"
            mr="2px"
        />
    );
};

export const Option = (props: any) => {
    const currentColors = useCurrentColors();
    const CustomCheckBox = styled.label<{
        $currentColors: any;
    }>`
        .container {
            display: block;
            position: relative;
            cursor: pointer;
            font-size: 13px;
            color: ${(props) => props.$currentColors.brand['300']};
            padding-left: 25px;
            line-height: 13px;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
        }

        /* Hide the browser's default checkbox */
        .container input {
            position: absolute;
            opacity: 0;
            cursor: pointer;
            height: 0;
            width: 0;
        }

        /* Create a custom checkbox */
        .checkmark {
            position: absolute;
            top: 0;
            left: 0;
            height: 13px;
            width: 13px;
            background-color: #e2e8f0;
        }

        // /* On mouse-over, add a grey background color */
        // .container:hover input ~ .checkmark {
        //     background-color: #ccc;
        // }

        /* When the checkbox is checked, add a green background */
        .container input:checked ~ .checkmark {
            background-color: ${(props) => props.$currentColors.primaryColor};
        }

        /* Create the checkmark/indicator (hidden when not checked) */
        .checkmark:after {
            content: '';
            position: absolute;
            display: none;
        }

        /* Show the checkmark when checked */
        .container input:checked ~ .checkmark:after {
            display: block;
        }

        /* Style the checkmark/indicator */
        .container .checkmark:after {
            left: 4px;
            top: 1px;
            width: 5px;
            height: 8px;
            border: solid white;
            border-width: 0 2px 2px 0;
            -webkit-transform: rotate(45deg);
            -ms-transform: rotate(45deg);
            transform: rotate(45deg);
        }
    `;
    return (
        <div>
            <components.Option {...props}>
                <CustomCheckBox $currentColors={currentColors}>
                    <label className="container">
                        {props.label}
                        <input
                            type="checkbox"
                            value={props.label}
                            checked={props.isSelected}
                            onChange={() => null}
                        />
                        <span className="checkmark"></span>
                    </label>
                </CustomCheckBox>
            </components.Option>
        </div>
    );
};

const ClearIndicator = (props: any) => {
    const {
        getStyles,
        innerProps: { ref, ...restInnerProps },
    } = props;
    return (
        <Box
            {...restInnerProps}
            ref={ref}
            style={getStyles('clearIndicator', props) as CSSProperties}
            py="0px !important"
        >
            <Icon
                aria-label="clear"
                name="ActClose"
                my="10px"
                mx="4px"
                width="20px"
                height="20px"
            />
        </Box>
    );
};

const MultiValueRemove = (props: any) => {
    const currentColors = useCurrentColors();

    return (
        <components.MultiValueRemove
            {...props}
            cursor="pointer"
        >
            <Icon
                color={currentColors.brand['500']}
                name="ActClose"
                width="16px"
                height="16px"
                aria-label="remove"
                cursor="pointer"
            />
        </components.MultiValueRemove>
    );
};

const MultiValueLabel = (props: any) => {
    return (
        <components.MultiValueLabel {...props}>
            <CanopyTooltip
                label={props.data.displayName}
                width="100%"
            >
                {props.data.displayName || props.data.label}
            </CanopyTooltip>
        </components.MultiValueLabel>
    );
};
const SingleValue = (props: any) => {
    return (
        <components.SingleValue {...props}>
            <Flex
                direction="column"
                width="100%"
            >
                <Text
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                >
                    {props.data.displayName || props.data.label}
                </Text>
            </Flex>
        </components.SingleValue>
    );
};
export {
    getCommonStyles,
    getDropdownIndicator,
    getMultiSelectStyles,
    ClearIndicator,
    MultiValueLabel,
    SingleValue,
    MultiValueRemove,
};
